import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Phone, Instagram, Mail, Sparkles } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { name: 'בית', path: '/' },
    { name: 'קבוצות', path: '/groups' },
    { name: 'סדנאות', path: '/workshops' },
    { name: 'ליווי אישי', path: '/personal' },
    { name: 'המלצות', path: '/testimonials' },
    { name: 'בהזמנה אישית', path: '/custom-order' },
    { name: 'יצירת קשר', path: '/contact' }
  ];

  return (
    <footer className="bg-gradient-to-b from-nitzan-light-bg to-nitzan-sage/20 pt-16 pb-8">
      <div className="container-width">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/images/logo.png" 
                  alt="ניצן שרף בוסל" 
                  className="w-12 h-12 object-contain"
                />
                <span className="text-2xl font-bold gradient-text">ניצן שרף בוסל</span>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                מסע של גילוי עצמי, ריפוי וצמיחה אישית. 
                נומרולוגיה, תטא הילינג וליווי אישי.
              </p>
              <div className="flex items-center gap-2 text-nitzan-teal">
                <Sparkles size={20} />
                <span className="text-sm italic">מיקס ייחודי של רוח וקסם</span>
              </div>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="text-xl font-bold text-nitzan-teal mb-4">ניווט מהיר</h3>
              <ul className="space-y-2">
                {navLinks.slice(0, 3).map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-gray-700 hover:text-nitzan-teal transition-colors duration-300 text-right block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* More Links */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-xl font-bold text-nitzan-teal mb-4">שירותים</h3>
              <ul className="space-y-2">
                {navLinks.slice(3).map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-gray-700 hover:text-nitzan-teal transition-colors duration-300 text-right block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-xl font-bold text-nitzan-teal mb-4">יצירת קשר</h3>
              <div className="space-y-3">
                <a
                  href="tel:052-3918877"
                  className="flex items-center gap-3 text-gray-700 hover:text-nitzan-teal transition-colors duration-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-nitzan-mint/20 flex items-center justify-center group-hover:bg-nitzan-mint/40 transition-colors">
                    <Phone size={18} className="text-nitzan-teal" />
                  </div>
                  <span>052-3918877</span>
                </a>

                <a
                  href="https://instagram.com/nitzan_sb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-700 hover:text-nitzan-teal transition-colors duration-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-nitzan-mint/20 flex items-center justify-center group-hover:bg-nitzan-mint/40 transition-colors">
                    <Instagram size={18} className="text-nitzan-teal" />
                  </div>
                  <span>@nitzan_sb</span>
                </a>

                <a
                  href="mailto:nitzansb@gmail.com"
                  className="flex items-center gap-3 text-gray-700 hover:text-nitzan-teal transition-colors duration-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-nitzan-mint/20 flex items-center justify-center group-hover:bg-nitzan-mint/40 transition-colors">
                    <Mail size={18} className="text-nitzan-teal" />
                  </div>
                  <span>nitzansb@gmail.com</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-nitzan-sage/30 my-8"></div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-gray-600 text-center md:text-right">
            © {currentYear} ניצן שרף בוסל. כל הזכויות שמורות.
          </p>

          <a 
            href="https://mapu.co.il/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-gray-600 hover:text-nitzan-teal transition-colors duration-300"
          >
            <span>נבנה ב</span>
            <Heart size={16} className="text-nitzan-terracotta fill-nitzan-terracotta" />
            <span>על ידי מאפו בניית אתרים</span>
          </a>
        </motion.div>

        {/* Additional Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-gray-500 italic">
            "אני מאמינה שאנחנו האדונים והסמכות בחיים שלנו" ✨
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;

